<template>
  <v-container fluid fill-height class="driver-login-container">
    <v-row align="center" justify="center">
      <v-col cols="12" sm="8" md="6" lg="4">
        <v-card elevation="8" class="pa-4">
          <v-card-title class="text-h5 font-weight-bold text-center" style="color: rgb(16, 24, 32)">
            <v-icon large class="mr-2" style="color: rgb(16, 24, 32, 0.8)">mdi-truck-delivery</v-icon>
            <div>Driver Login</div>
          </v-card-title>

          <v-card-text class="pt-6">
            <v-form ref="loginForm" v-model="valid" @submit.prevent="handleLogin">
              <v-text-field
                v-model="credentials.email"
                label="Email"
                prepend-icon="mdi-email"
                :rules="[rules.required, rules.email]"
                outlined
                dense
                required
                autocomplete="email"
                type="email"
              />

              <v-text-field
                v-model="credentials.phone"
                label="Phone Number"
                prepend-icon="mdi-phone"
                :rules="[rules.required, rules.phone]"
                outlined
                dense
                required
                autocomplete="tel"
                placeholder="0123456789"
              />

              <v-checkbox
                v-model="rememberMe"
                label="Remember me"
                color="rgba(218, 41, 28, 0.4)"
                class="mt-0"
                dense
              />

              <v-alert
                v-if="errorMessage"
                type="error"
                dense
                dismissible
                class="mb-4"
                @input="errorMessage = ''"
              >
                {{ errorMessage }}
              </v-alert>

              <v-btn
                type="submit"
                color="rgba(218, 41, 28, 0.4)"
                dark
                block
                x-large
                :loading="loading"
                class="mt-4 text-none"
                elevation="2"
              >
                <v-icon left>mdi-login</v-icon>
                Login
              </v-btn>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import liveRequest from '@/utils/live_requests';

export default {
  name: 'DriverLogin',

  data() {
    return {
      valid: true,
      loading: false,
      errorMessage: '',
      rememberMe: false,
      credentials: {
        email: '',
        phone: '',
      },
      rules: {
        required: (value) => !!value || 'This field is required',
        email: (value) => {
          const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          return pattern.test(value) || 'Invalid email address';
        },
        phone: (value) => {
          // Remove spaces, dashes, and + signs for validation
          const cleaned = value.replace(/\s/g, '').replace(/-/g, '').replace(/\+/g, '');
          const pattern = /^[0-9]{8,15}$/;
          return pattern.test(cleaned) || 'Invalid phone number (8-15 digits)';
        },
      },
    };
  },

  mounted() {
    this.loadSavedCredentials();
  },

  methods: {
    async handleLogin() {
      if (!this.$refs.loginForm.validate()) return;

      this.loading = true;
      this.errorMessage = '';

      try {
        // Call driver login API endpoint using liveRequest
        const response = await liveRequest(
          'POST',
          'api/common/driver/login',
          null, // No token needed for login
          {
            email: this.credentials.email,
            phone: this.credentials.phone,
          },
          null // No query params
        );

        // Store driver authentication token
        localStorage.setItem('driver_token', response.data.token);
        localStorage.setItem('driver_info', JSON.stringify(response.data.driver));

        // Save credentials if "Remember Me" is checked
        if (this.rememberMe) {
          localStorage.setItem('driver_remember_me', 'true');
          localStorage.setItem('driver_saved_email', this.credentials.email);
          localStorage.setItem('driver_saved_phone', this.credentials.phone);
        } else {
          // Clear saved credentials if "Remember Me" is unchecked
          localStorage.removeItem('driver_remember_me');
          localStorage.removeItem('driver_saved_email');
          localStorage.removeItem('driver_saved_phone');
        }

        // Redirect to driver dashboard
        this.$router.push({ name: 'driver' });
      } catch (error) {
        console.error('Driver login error:', error);
        this.errorMessage =
          error.response?.data?.message ||
          'Invalid email or phone number. Please verify your credentials.';
      } finally {
        this.loading = false;
      }
    },

    loadSavedCredentials() {
      // Check if "Remember Me" was previously enabled
      const rememberMe = localStorage.getItem('driver_remember_me') === 'true';
      if (rememberMe) {
        this.rememberMe = true;
        this.credentials.email = localStorage.getItem('driver_saved_email') || '';
        this.credentials.phone = localStorage.getItem('driver_saved_phone') || '';
      }
    },

    handleForgotPassword() {
      // TODO: Implement forgot credentials flow
      alert('Please contact your administrator to verify your credentials.');
    },

    goToMainApp() {
      this.$router.push({ name: 'landing' });
    },
  },
};
</script>

<style scoped lang="scss">
.driver-login-container {
  background: rgba(84, 88, 90, 0.2);
  min-height: 100vh;
}
</style>

